import { create } from 'zustand'

/**
 * Императивный confirm-диалог взамен нативного window.confirm().
 *
 * Использование (в любом компоненте/хуке/модуле):
 *   import { confirm } from '@/shared/ui/confirmStore'
 *   if (!(await confirm({ title: 'Удалить заявку?', danger: true }))) return
 *
 * Хост <ConfirmHost/> смонтирован один раз в App. Store глобальный — provider
 * не нужен, функция работает вне React-контекста (например, в queueActions).
 */
export interface ConfirmOptions {
  title: string
  /** Пояснение под заголовком (необязательно). */
  message?: string
  /** Текст кнопки подтверждения. По умолчанию «Да». */
  confirmText?: string
  /** Текст кнопки отмены. По умолчанию «Отмена». */
  cancelText?: string
  /** Красная кнопка подтверждения — для необратимых действий (удаление). */
  danger?: boolean
}

interface ConfirmRequest extends ConfirmOptions {
  resolve: (value: boolean) => void
}

interface ConfirmState {
  current: ConfirmRequest | null
  /** Открыть диалог. Промис резолвится true (подтвердил) / false (отменил). */
  open: (options: ConfirmOptions) => Promise<boolean>
  /** Закрыть с результатом — вызывается хостом по клику на кнопку/фон. */
  settle: (value: boolean) => void
}

export const useConfirmStore = create<ConfirmState>((set, get) => ({
  current: null,
  open: (options) =>
    new Promise<boolean>((resolve) => {
      set({ current: { ...options, resolve } })
    }),
  settle: (value) => {
    const { current } = get()
    if (current) current.resolve(value)
    set({ current: null })
  },
}))

/** Удобная обёртка: `await confirm({ title })`. */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  return useConfirmStore.getState().open(options)
}
